import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AnimateHeight from 'react-animate-height';
import { COND_KEY_SET, CondKey, useSearchConds } from '../../hooks/useSearchConds';

type SearchCondsProps = {
  clearResults: () => void;
}

const SearchConds = (props: SearchCondsProps) => {
  const [ showConds, setShowConds ] = useState(
    !(window.matchMedia && window.matchMedia('(max-device-width: 750px)').matches)
  );
  const { conds, setCond, resetConds, stringifyParams } = useSearchConds();
  const navigate = useNavigate();
  const search = useLocation().search;

  useEffect(() => {
    const query = new URLSearchParams(search);
    resetConds(query);
  }, [ search, resetConds ]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const condKey: string = e.target.name || "";

    if (e.target.checked) {
      setCond(condKey, e.target.value);
    } else {
      setCond(condKey, e.target.value, false);
    }
  }

  const condItems = (condKey: CondKey) => {
    return Object.keys(COND_KEY_SET[condKey]).map((key, idx) => {
      return (
        <span className={ `wpcf7-list-item ${ idx === 0 ? 'first' : '' }` } key={ key } >
          <label>
            <input
              type="checkbox"
              name={ condKey }
              value={ key }
              onChange={ handleChange }
              checked={ (conds[condKey] && conds[condKey].includes(key)) || false }
            />
            <span className="wpcf7-list-item-label">{ COND_KEY_SET[condKey][key] }</span>
          </label>
        </span>
      )
    })
  }

  const handleSubmit = () => {
    props.clearResults();
    setShowConds(
      !(window.matchMedia && window.matchMedia('(max-device-width: 750px)').matches)
    )
    navigate(`/search-result?${ stringifyParams() }&_t=${ Date.now() }`);
  }

  const handleClear = () => {
    props.clearResults();
    resetConds();
    navigate(`/search-result?_t=${ Date.now() }`);
  }

  return (
    <aside>
      <div className="form">
        <div className="bluebtn smonly" onClick={ () => setShowConds(!showConds) }>検索条件を変更</div>
        <AnimateHeight
          duration={ 500 }
          height={ showConds ? 'auto' : 0 }
        >
          <dl className="graf_list">
            <dt>本人確認</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('kyc_certified_only') }
                </span>
              </span>
            </dd>
            <dt>エリア</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('area') }
                </span>
              </span>
            </dd>
            <dt>年齢</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('age') }
                </span>
              </span>
            </dd>
            <dt>血液型</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('blood_type') }
                </span>
              </span>
            </dd>
            <dt>身長</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('height') }
                </span>
              </span>
            </dd>
            <dt>体型</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('body_figure') }
                </span>
              </span>
            </dd>
            <dt>まぶた</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('eyelid') }
                </span>
              </span>
            </dd>
            <dt>髪質</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('hair_type') }
                </span>
              </span>
            </dd>
            <dt>髪色</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('hair_color') }
                </span>
              </span>
            </dd>
            <dt>最終学歴</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('college_type') }
                </span>
              </span>
            </dd>
            <dt>理系・文系</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('major_type') }
                </span>
              </span>
            </dd>
            <dt>婚姻歴</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('marriage') }
                </span>
              </span>
            </dd>
            <dt>子供</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('children') }
                </span>
              </span>
            </dd>
            <dt>国籍</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('nationality') }
                </span>
              </span>
            </dd>
            <dt>人種</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('race') }
                </span>
              </span>
            </dd>
            <dt>性病検査</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('std_certified_only') }
                </span>
              </span>
            </dd>
            <dt>精子品質</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('semen_total') }
                </span>
              </span>
            </dd>
            <dt>提供方法</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('methods') }
                </span>
              </span>
            </dd>
            <dt>交通費・宿泊代</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('travel_expenses') }
                </span>
              </span>
            </dd>
            <dt>身元の開示</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('identity_disclosure') }
                </span>
              </span>
            </dd>
            <dt>証明書の提示</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('certificate_presentation') }
                </span>
              </span>
            </dd>
            <dt>出自を知る権利</dt>
            <dd className="flex">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { condItems('origin_right') }
                </span>
              </span>
            </dd>
          </dl>
        </AnimateHeight>
        <input type="submit" value="こだわり検索" className="orangebtn searchbtn" onClick={ handleSubmit } />
        <input type="submit" value="条件をクリア" className="w_bluebtn searchbtn" onClick={ handleClear } />
      </div>
    </aside>
  );
}

export default SearchConds;
